import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';
import { useModal } from '../context/ModalContext';
import DashboardLayout from '../components/DashboardLayout';
import { Reservation, ReservationSource, ReservationStatus } from '../types';
import { api, centsToDollars } from '../api/client';
import { BOOKING_STATUS } from '../constants/checkout';

import courtReserveLogo from '../assets/integrations/courtreserve.svg';
import playByPointLogo from '../assets/integrations/playbypoint.webp';
import googleCalendarLogo from '../assets/integrations/google-calendar.png';

const INTEGRATION_META: Record<string, { name: string; logo: string }> = {
  courtreserve: { name: 'CourtReserve', logo: courtReserveLogo },
  playbypoint: { name: 'PlayByPoint', logo: playByPointLogo },
  'google-calendar': { name: 'Google Calendar', logo: googleCalendarLogo },
};

// Court color palette - distinct colors for easy differentiation
const COURT_COLORS = [
  { bg: '#e6f2e9', border: '#236332', text: '#1a4d26' },  // Green
  { bg: '#e8edfc', border: '#2f58dd', text: '#2445b3' },  // Blue
  { bg: '#fcf0e9', border: '#dd7d47', text: '#c46835' },  // Orange
  { bg: '#f9fce6', border: '#b8c410', text: '#8a9a0d' },  // Lime
  { bg: '#fce6f2', border: '#dd47a0', text: '#b83580' },  // Pink
  { bg: '#e6f9fc', border: '#47c4dd', text: '#35a0b8' },  // Cyan
  { bg: '#f2e6fc', border: '#9447dd', text: '#7535b8' },  // Purple
  { bg: '#fcf6e6', border: '#ddaa47', text: '#b88935' },  // Gold
];

// Primary is the canonical status; legacy members tolerated for cached data.
const PENDING_STATUSES = new Set([BOOKING_STATUS.PENDING_APPROVAL, 'pending', 'manual_booking_approval']);
const CONFIRMED_STATUSES = new Set(['approved', 'confirmed']);
const CANCELED_STATUSES = new Set(['canceled', 'declined', 'expired']);

const statusLabel = (status: string): string =>
  status.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

const getReservationTone = (status: string) => {
  if (PENDING_STATUSES.has(status)) {
    return {
      bg: '#fef3c7',
      border: '#f59e0b',
      text: '#92400e',
      badgeBg: '#fef3c7',
      badgeText: '#92400e',
      label: 'Pending Approval',
      isPending: true,
    };
  }
  if (CONFIRMED_STATUSES.has(status)) {
    return {
      bg: '#dcfce7',
      border: '#16a34a',
      text: '#166534',
      badgeBg: '#dcfce7',
      badgeText: '#166534',
      label: 'Confirmed',
      isPending: false,
    };
  }
  if (CANCELED_STATUSES.has(status)) {
    return {
      bg: '#fee2e2',
      border: '#ef4444',
      text: '#991b1b',
      badgeBg: '#fee2e2',
      badgeText: '#991b1b',
      label: statusLabel(status),
      isPending: false,
    };
  }
  return {
    bg: '#f3f4f6',
    border: '#9ca3af',
    text: '#4b5563',
    badgeBg: '#f3f4f6',
    badgeText: '#4b5563',
    label: statusLabel(status),
    isPending: false,
  };
};

const getCourtDisplayLabel = (courtName: string): string => {
  return courtName;
};

const getZonedDateParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = Number(parts.find(part => part.type === 'year')?.value || '0');
  const month = Number(parts.find(part => part.type === 'month')?.value || '1');
  const day = Number(parts.find(part => part.type === 'day')?.value || '1');

  return { year, month, day };
};

const getZonedTimeParts = (date: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const hour = Number(parts.find(part => part.type === 'hour')?.value || '0');
  const minute = Number(parts.find(part => part.type === 'minute')?.value || '0');

  return { hour, minute };
};

const getZonedDateString = (date: Date, timeZone: string) => {
  const { year, month, day } = getZonedDateParts(date, timeZone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const getDateFromZonedToday = (date: Date, timeZone: string) => {
  const { year, month, day } = getZonedDateParts(date, timeZone);
  return new Date(year, month - 1, day);
};

export default function DashboardScreen() {
  const { user, isSuperAdmin } = useAuth();
  const { business, loading, needsVenueSelection, reservations, updateReservation, refreshData } = useBusiness();
  const { showAlert, showDanger, showConfirm } = useModal();
  const navigate = useNavigate();
  const venueTimezone = business?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showEditModal, setShowEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const calendarScrollRef = useRef<HTMLDivElement>(null);


  // Slot data: maps courtId -> array of all slots with status and pricing
  const [slotsByCourtId, setSlotsByCourtId] = useState<Record<string, Array<{
    start_time: string;
    end_time: string;
    hourly_rate_cents: number;
    status: 'available' | 'unavailable' | 'booked';
    isOverride?: boolean;
    booking?: {
      id: string;
      confirmationCode: string;
      customerName: string;
      status: string;
      notes?: string;
    };
  }>>>({});

  // Integration sync state
  const [integrationInfo, setIntegrationInfo] = useState<{
    integrationId: string;
    integrationType: string;
    lastSyncAt: string | null;
    syncStatus: string;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Update current time every minute for the time indicator
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // Fetch slot data when date changes
  const fetchSlots = async () => {
    if (!business) return;
    try {
      const y = currentDate.getFullYear();
      const m = String(currentDate.getMonth() + 1).padStart(2, '0');
      const d = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      const result = await api.getAvailabilityForDate(dateStr);
      setSlotsByCourtId(result.slotsByCourtId);
      if (result.integration) {
        setIntegrationInfo(result.integration);
      }
    } catch (err) {
      console.error('Failed to fetch slots:', err);
      setSlotsByCourtId({});
    }
  };

  useEffect(() => {
    fetchSlots();
  }, [currentDate, business]);

  // Clean up sync polling on unmount
  useEffect(() => {
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
    };
  }, []);


  // Form state for editing existing reservations
  const [formData, setFormData] = useState({
    courtId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    date: '',
    startTime: '',
    durationHours: 1,
    status: 'confirmed' as ReservationStatus,
    source: 'manual' as ReservationSource,
    notes: '',
  });

  const currentDateStr = useMemo(() => {
    const y = currentDate.getFullYear();
    const m = String(currentDate.getMonth() + 1).padStart(2, '0');
    const d = String(currentDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [currentDate]);

  const currentDateReservations = useMemo(
    () =>
      reservations
        .filter(r => r.date === currentDateStr)
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [reservations, currentDateStr]
  );

  const reservationSections = useMemo(() => {
    const requested: Reservation[] = [];
    const confirmed: Reservation[] = [];
    const canceled: Reservation[] = [];
    const other: Reservation[] = [];

    currentDateReservations.forEach(res => {
      const status = res.status.toLowerCase();
      if (PENDING_STATUSES.has(status)) {
        requested.push(res);
      } else if (CONFIRMED_STATUSES.has(status)) {
        confirmed.push(res);
      } else if (CANCELED_STATUSES.has(status)) {
        canceled.push(res);
      } else {
        other.push(res);
      }
    });

    return [
      { key: 'requested', title: 'Requested', count: requested.length, items: requested },
      { key: 'confirmed', title: 'Approved / Confirmed', count: confirmed.length, items: confirmed },
      { key: 'canceled', title: 'Canceled / Declined', count: canceled.length, items: canceled },
      { key: 'other', title: 'Other', count: other.length, items: other },
    ].filter(section => section.count > 0);
  }, [currentDateReservations]);

  // Redirect SUPERADMIN to venue selection if needed
  useEffect(() => {
    if (needsVenueSelection && isSuperAdmin) {
      navigate('/superadmin');
    }
  }, [needsVenueSelection, isSuperAdmin, navigate]);

  // Auto-scroll: anchor current time ~30% from top on today, or business open on other days
  useEffect(() => {
    if (!calendarScrollRef.current || !business) return;
    const now = new Date();
    const todayStr = getZonedDateString(now, venueTimezone);
    const viewingDateStr = currentDateStr;
    const isViewingToday = viewingDateStr === todayStr;
    const rowHeight = 36; // Each 30-minute row
    const gridStartHour = 5; // Grid starts at 5 AM

    setTimeout(() => {
      if (!calendarScrollRef.current) return;
      const containerHeight = calendarScrollRef.current.clientHeight;

      if (isViewingToday) {
        const { hour: nowHour, minute: nowMin } = getZonedTimeParts(now, venueTimezone);
        const totalMinutesFromStart = (nowHour - gridStartHour) * 60 + nowMin;
        const redLinePos = (totalMinutesFromStart / 30) * rowHeight;
        calendarScrollRef.current.scrollTop = Math.max(0, redLinePos - containerHeight * 0.3);
      } else {
        // Scroll to 1 hour before business open hour so opening time is clearly visible
        const dow = currentDate.getDay();
        const dayHours = business.operatingHours?.find(h => h.dayOfWeek === dow);
        const open = dayHours && !dayHours.isClosed ? parseInt(dayHours.openTime.split(':')[0]) : 8;
        const scrollToHour = Math.max(gridStartHour, open - 1);
        calendarScrollRef.current.scrollTop = (scrollToHour - gridStartHour) * 2 * rowHeight;
      }
    }, 100);
  }, [business, currentDate, currentDateStr, venueTimezone]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading" style={{ minHeight: '400px' }}>
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (needsVenueSelection) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Select a Venue</h1>
          <p>As a Super Admin, please select a venue to manage from the All Venues page.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/superadmin')}>
          Go to All Venues
        </button>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Welcome, {user?.name}!</h1>
          <p>Set up your facility to start accepting reservations</p>
        </div>
      </DashboardLayout>
    );
  }

  const activeCourts = business.courts.filter(c => c.isActive).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true })
  );
  const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const todayBase = new Date();
  const todayStr = getZonedDateString(todayBase, venueTimezone);
  const maxDate = getDateFromZonedToday(todayBase, venueTimezone);
  maxDate.setDate(maxDate.getDate() + 14);
  // Derived above with useMemo to keep hook order consistent across renders.
  const timeSlots = Array.from({ length: 38 }, (_, i) => ({
    hour: Math.floor(i / 2) + 5,
    minute: (i % 2) * 30,
  })); // 5:00 AM to 11:30 PM in 30-minute intervals (extra padding at both ends)

  // Get operating hours for the current day
  const dayOfWeek = currentDate.getDay(); // 0 = Sunday
  const todayHours = business.operatingHours?.find(h => h.dayOfWeek === dayOfWeek);
  const hasOperatingHours = business.operatingHours && business.operatingHours.length > 0;
  const parseHourForSchedule = (time: string, isCloseTime = false): number => {
    const [hStr, mStr] = time.split(':');
    const hour = Number(hStr);
    const minute = Number(mStr || '0');
    // Midnight close means end-of-day, not start-of-day.
    if (isCloseTime && hour === 0 && minute === 0) return 24;
    return hour + (minute / 60);
  };
  const openHour = todayHours && !todayHours.isClosed ? parseHourForSchedule(todayHours.openTime) : null;
  const closeHour = todayHours && !todayHours.isClosed ? parseHourForSchedule(todayHours.closeTime, true) : null;
  const isClosed = hasOperatingHours ? (todayHours?.isClosed ?? true) : false;

  // Current time indicator (only show on today)
  const isToday = currentDateStr === todayStr;
  const { hour: currentHour, minute: currentMinute } = getZonedTimeParts(currentTime, venueTimezone);
  const currentSlotMinute = currentMinute >= 30 ? 30 : 0;
  const currentTimePercent = ((currentMinute - currentSlotMinute) / 30) * 100; // Position within the 30-min cell

  // Get court color by index
  const getCourtColor = (courtIndex: number) => COURT_COLORS[courtIndex % COURT_COLORS.length];

  // Get court index by ID
  const getCourtIndex = (courtId: string) => activeCourts.findIndex(c => c.id === courtId);

  const getCourtPillStyle = (courtId: string): React.CSSProperties => {
    const idx = getCourtIndex(courtId);
    const color = idx >= 0 ? getCourtColor(idx) : { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' };
    return {
      fontSize: '11px',
      fontWeight: 700,
      color: color.text,
      background: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: '999px',
      padding: '2px 8px',
      lineHeight: 1.2,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
    };
  };

  const getScheduleCourtPillStyle = (courtId: string): React.CSSProperties => {
    const idx = getCourtIndex(courtId);
    const color = idx >= 0 ? getCourtColor(idx) : { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' };
    return {
      fontSize: '9px',
      fontWeight: 700,
      color: color.text,
      background: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: '999px',
      padding: '1px 6px',
      lineHeight: 1.2,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
    };
  };

  // Navigation
  const navigateDay = (direction: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(getDateFromZonedToday(new Date(), venueTimezone));

  // Handle refresh (triggers integration sync if available, otherwise just re-fetches slots)
  const handleRefresh = async () => {
    if (syncing) return;
    setSyncing(true);
    if (integrationInfo) {
      try {
        await api.triggerSync(integrationInfo.integrationId);
        syncPollRef.current = setInterval(async () => {
          try {
            const status = await api.getSyncStatus(integrationInfo.integrationId);
            if (status.sync_status === 'succeeded' || status.sync_status === 'failed' || status.sync_status === 'idle') {
              setSyncing(false);
              if (syncPollRef.current) clearInterval(syncPollRef.current);
              syncPollRef.current = null;
              await refreshData();
              fetchSlots();
            }
          } catch {
            setSyncing(false);
            if (syncPollRef.current) clearInterval(syncPollRef.current);
            syncPollRef.current = null;
          }
        }, 2000);
      } catch {
        setSyncing(false);
        await refreshData();
        fetchSlots();
      }
    } else {
      await refreshData();
      await fetchSlots();
      setSyncing(false);
    }
  };

  const formatTimeAgo = (dateStr: string | null): string => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const formatScheduleLabel = (hour: number, minute: number): string => {
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${suffix}`;
  };

  // Get all reservations for a specific time slot (across all courts)
  const getReservationsAtTime = (date: string, hour: number) => {
    return reservations.filter(r => {
      if (r.date !== date) return false;
      const startHour = parseInt(r.startTime.split(':')[0]);
      const endHour = parseInt(r.endTime.split(':')[0]);
      return hour >= startHour && hour < endHour;
    });
  };

  // Check if this is the start slot for a reservation
  const isReservationStart = (date: string, courtId: string, hour: number, minute: number) => {
    const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    return reservations.find(r => r.date === date && r.courtId === courtId && r.startTime === timeStr && r.status !== 'canceled');
  };

  // Get reservation at specific time slot for a court
  const getReservationAtTime = (date: string, courtId: string, hour: number, minute: number) => {
    const slotMinutes = hour * 60 + minute;
    return reservations.find(r => {
      if (r.date !== date || r.courtId !== courtId || r.status === 'canceled') return false;
      const [startH, startM] = r.startTime.split(':').map(Number);
      const [endH, endM] = r.endTime.split(':').map(Number);
      return slotMinutes >= (startH * 60 + startM) && slotMinutes < (endH * 60 + endM);
    });
  };

  // Get slot info for a specific 30-minute time slot
  const getSlotAtTime = (courtId: string, hour: number, minute: number) => {
    const slots = slotsByCourtId[courtId];
    if (!slots || slots.length === 0) return null;

    const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
    return slots.find(s => timeStr >= s.start_time && timeStr < s.end_time) || null;
  };

  // Handle clicking on a slot
  const handleSlotClick = (date: string, hour: number, minute: number, courtId: string) => {
    const reservation = getReservationAtTime(date, courtId, hour, minute);
    if (reservation) {
      setSelectedReservation(reservation);
      setFormData({
        courtId: reservation.courtId,
        customerName: reservation.customerName,
        customerEmail: reservation.customerEmail || '',
        customerPhone: reservation.customerPhone || '',
        date: reservation.date,
        startTime: reservation.startTime,
        durationHours: reservation.durationHours,
        status: reservation.status,
        source: reservation.source,
        notes: reservation.notes || '',
      });
      setIsEditing(false);
      setShowEditModal(true);
    } else {
      showAlert(
        'Manual Booking Creation Removed',
        'Create bookings through the customer checkout flow. Empty calendar slots are no longer bookable from the admin dashboard.',
        'info'
      );
    }
  };

  const handleUpdateReservation = async () => {
    if (!selectedReservation) return;

    const [startH2, startM2] = formData.startTime.split(':').map(Number);
    const totalMin2 = startH2 * 60 + startM2 + formData.durationHours * 60;
    const endH2 = Math.floor(totalMin2 / 60);
    const endM2 = totalMin2 % 60;
    const endTime = `${endH2.toString().padStart(2, '0')}:${endM2.toString().padStart(2, '0')}`;

    try {
      await updateReservation(selectedReservation.id, {
        courtId: formData.courtId,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail || undefined,
        customerPhone: formData.customerPhone || undefined,
        date: formData.date,
        startTime: formData.startTime,
        endTime,
        durationHours: formData.durationHours,
        status: formData.status,
        source: formData.source,
        notes: formData.notes,
      });
      setShowEditModal(false);
      setSelectedReservation(null);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update reservation', 'error');
    }
  };

  const handleCancelReservation = async () => {
    if (!selectedReservation) return;

    const confirmed = await showConfirm(
      'Cancel Reservation',
      `Are you sure you want to cancel this reservation for ${selectedReservation.customerName}? The customer will be notified.`,
      { confirmLabel: 'Yes, Cancel', type: 'warning' }
    );

    if (confirmed) {
      try {
        await updateReservation(selectedReservation.id, { status: 'canceled' });
        setShowEditModal(false);
        setSelectedReservation(null);
      } catch (err) {
        await showAlert('Error', err instanceof Error ? err.message : 'Failed to cancel reservation', 'error');
      }
    }
  };

  return (
    <DashboardLayout>
      <div className="page-header">
        <h1>Schedule</h1>
      </div>

      <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 160px)', minHeight: '400px' }}>
      <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <button onClick={() => navigateDay(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', display: 'flex', borderRadius: '4px' }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827', minWidth: '130px', textAlign: 'center' }}>
            {currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
          <button onClick={() => navigateDay(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#9ca3af', display: 'flex', borderRadius: '4px' }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div style={{ position: 'relative', marginLeft: '4px', display: 'inline-flex', alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ cursor: 'pointer' }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <input
              type="date"
              value={currentDateStr}
              min={todayStr}
              max={formatDate(maxDate)}
              onChange={e => {
                const parts = e.target.value.split('-').map(Number);
                if (parts.length === 3) setCurrentDate(new Date(parts[0], parts[1] - 1, parts[2]));
              }}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
            />
          </div>
          {currentDateStr !== todayStr && (
            <button onClick={goToToday} style={{ background: 'none', border: '1px solid #e5e7eb', color: '#6b7280', fontSize: '11px', fontWeight: 500, cursor: 'pointer', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>
              Today
            </button>
          )}
          <div style={{ flex: 1 }} />
          {integrationInfo && (() => {
            const meta = INTEGRATION_META[integrationInfo.integrationType];
            return meta ? (
              <button
                onClick={handleRefresh}
                disabled={syncing}
                title={syncing ? 'Syncing...' : 'Click to sync now'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '3px 10px 3px 6px', borderRadius: '999px',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                  border: '1px solid #bbf7d0', cursor: syncing ? 'default' : 'pointer',
                  fontSize: '11px', fontWeight: 500, color: '#166534',
                  transition: 'all 0.15s ease',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={e => { if (!syncing) { e.currentTarget.style.background = 'linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)'; } }}
                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'; }}
              >
                <img src={meta.logo} alt={meta.name} style={{ width: '14px', height: '14px', borderRadius: '3px', objectFit: 'contain' }} />
                <span style={{ whiteSpace: 'nowrap' }}>
                  {syncing ? 'Syncing...' : `Synced from ${meta.name}`}
                </span>
                {integrationInfo.lastSyncAt && !syncing && (
                  <span style={{ color: '#4ade80', fontSize: '10px', fontWeight: 400 }}>
                    {formatTimeAgo(integrationInfo.lastSyncAt)}
                  </span>
                )}
                {syncing ? (
                  <span style={{ width: '10px', height: '10px', border: '1.5px solid #166534', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                ) : (
                  <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="#22c55e" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M14 2v4h-4M2 14v-4h4" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.5 6A6 6 0 0 0 3.8 3.8L2 6M2.5 10a6 6 0 0 0 9.7 2.2L14 10" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </button>
            ) : null;
          })()}
          {!integrationInfo && (
            <button onClick={handleRefresh} disabled={syncing} style={{ background: 'none', border: 'none', cursor: syncing ? 'default' : 'pointer', color: syncing ? '#d1d5db' : '#9ca3af', display: 'flex', padding: '2px', marginLeft: '4px' }}>
              {syncing ? (
                <span style={{ width: '12px', height: '12px', border: '1.5px solid #9ca3af', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
              ) : (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2v4h-4M2 14v-4h4" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.5 6A6 6 0 0 0 3.8 3.8L2 6M2.5 10a6 6 0 0 0 9.7 2.2L14 10" strokeLinecap="round" strokeLinejoin="round"/></svg>
              )}
            </button>
          )}
        </div>

        {/* Grid */}
        <div ref={calendarScrollRef} style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: `${76 + activeCourts.length * 150}px` }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <th style={{ width: '76px', minWidth: '76px', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', left: 0, zIndex: 11 }} />
                {activeCourts.map((court) => {
                  return (
                    <th key={court.id} style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: '#111827', background: '#fff', borderBottom: '1px solid #e5e7eb', minWidth: '150px' }}>
                      <span style={getCourtPillStyle(court.id)}>
                        {getCourtDisplayLabel(court.name)}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {timeSlots.map(({ hour, minute }) => {
                const isHour = minute === 0;
                const slotHour = hour + (minute / 60);
                const outside = hasOperatingHours
                  ? (isClosed || openHour === null || closeHour === null || slotHour < openHour || slotHour >= closeHour)
                  : false;
                const isCurrent = isToday && hour === currentHour && minute === currentSlotMinute;

                return (
                  <tr key={`${hour}:${minute}`} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{
                      padding: 0, width: '76px', background: '#fff', borderRight: '1px solid #e5e7eb',
                      position: 'sticky', left: 0, zIndex: 5, height: '48px'
                    }}>
                      <span style={{
                        position: 'absolute', top: '-1px', right: '8px', transform: 'translateY(-50%)',
                        fontSize: '10px', fontWeight: 500, color: outside ? '#9ca3af' : '#6b7280',
                        background: '#fff', padding: '0 2px', lineHeight: '12px', zIndex: 6,
                      }}>
                        {formatScheduleLabel(hour, minute)}
                      </span>
                      {isCurrent && (
                        <div style={{ position: 'absolute', top: `${currentTimePercent}%`, left: 0, right: 0, height: '2px', background: '#ef4444', zIndex: 20 }}>
                          <div style={{ position: 'absolute', right: '-1px', top: '-3px', width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                        </div>
                      )}
                    </td>
                    {activeCourts.map((court, ci) => {
                      const res = isReservationStart(currentDateStr, court.id, hour, minute);
                      const hasRes = getReservationAtTime(currentDateStr, court.id, hour, minute);

                      if (hasRes && !res) {
                        const tone = getReservationTone(hasRes.status);
                        return (
                          <td key={court.id} onClick={() => handleSlotClick(currentDateStr, hour, minute, court.id)} style={{
                            padding: 0, background: '#fff', borderLeft: 'none',
                            cursor: 'pointer', position: 'relative', height: '48px', borderRight: '1px solid #f3f4f6'
                          }}>
                            {isCurrent && <div style={{ position: 'absolute', top: `${currentTimePercent}%`, left: 0, right: 0, height: '2px', background: '#ef4444', zIndex: 20 }} />}
                          </td>
                        );
                      }

                      const slot = !hasRes && !outside ? getSlotAtTime(court.id, hour, minute) : null;
                      const bg = outside ? '#fafafa' : slot?.status === 'unavailable' ? '#f5f5f5' : '#fff';
                      const clickable = !outside && !hasRes;

                      return (
                        <td key={court.id} onClick={() => clickable && handleSlotClick(currentDateStr, hour, minute, court.id)} style={{
                          padding: 0,
                          verticalAlign: 'top',
                          background: res ? '#fff' : bg,
                          borderLeft: 'none',
                          position: 'relative', height: '48px', borderRight: '1px solid #f3f4f6',
                          cursor: clickable || res ? 'pointer' : 'default'
                        }}>
                          {isCurrent && <div style={{ position: 'absolute', top: `${currentTimePercent}%`, left: 0, right: 0, height: '2px', background: '#ef4444', zIndex: 20 }} />}
                          {slot?.status === 'available' && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                              <span style={{ fontSize: '9px', fontWeight: 500, color: '#9ca3af' }}>${centsToDollars(slot.hourly_rate_cents)}</span>
                            </div>
                          )}
                          {slot?.status === 'unavailable' && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                              <span style={{ fontSize: '9px', fontWeight: 500, color: '#9ca3af' }}>Booked</span>
                            </div>
                          )}
                          {res && (() => {
                            const fmtT = (t: string) => {
                              const [h, m] = t.split(':').map(Number);
                              const suffix = h >= 12 ? 'p' : 'a';
                              const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
                              return m === 0 ? `${hr}${suffix}` : `${hr}:${m.toString().padStart(2, '0')}${suffix}`;
                            };
                            const slots = Math.max(res.durationHours * 2, 2);
                            const ROW_H = 48;
                            const slotHeight = slots * ROW_H + (slots - 1);
                            const tone = getReservationTone(res.status);
                            const isAwaitingRes = tone.isPending;
                            const scheduleCourtName = activeCourts.find(c => c.id === res.courtId)?.name || 'Unknown';
                            const scheduleCourtLabel = getCourtDisplayLabel(scheduleCourtName);
                            // Uniform scale: same ratio for X and Y so card looks proportional
                            const cardNaturalHeight = 130;
                            const scale = Math.min(slotHeight / cardNaturalHeight, 1);
                            return (
                              <div onClick={e => { e.stopPropagation(); handleSlotClick(currentDateStr, hour, minute, court.id); }} style={{
                                position: 'absolute', top: 0, left: 0, right: 0,
                                height: `${slotHeight}px`,
                                cursor: 'pointer', overflow: 'hidden', zIndex: 10,
                              }}>
                                {/* Uniformly scaled card – mirrors side panel card design */}
                                <div style={{
                                  width: `${100 / scale}%`,
                                  height: `${slotHeight / scale}px`,
                                  transform: `scale(${scale})`,
                                  transformOrigin: 'top left',
                                  background: '#fff',
                                  borderTop: `3px solid ${tone.border}`,
                                  borderLeft: `3px ${isAwaitingRes ? 'dashed' : 'solid'} ${tone.border}`,
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.03)',
                                  overflow: 'hidden',
                                }}>
                                  <div style={{ padding: '8px 10px' }}>
                                    {/* Header: time + status */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={tone.border} strokeWidth="2.5" style={{ flexShrink: 0, opacity: 0.7 }}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>
                                          {fmtT(res.startTime)} - {fmtT(res.endTime)}
                                        </span>
                                      </div>
                                      <span style={{
                                        fontSize: '8px', fontWeight: 700, padding: '1px 6px', borderRadius: '999px',
                                        letterSpacing: '0.3px', textTransform: 'uppercase',
                                        background: tone.badgeBg, color: tone.badgeText,
                                      }}>
                                        {tone.label}
                                      </span>
                                    </div>

                                    {/* Customer name */}
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.01em' }}>
                                      {res.customerName}
                                    </div>

                                    {/* Contact info */}
                                    {(res.customerEmail || res.customerPhone) && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', marginBottom: '5px' }}>
                                        {res.customerEmail && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                                            <span style={{ fontSize: '10px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{res.customerEmail}</span>
                                          </div>
                                        )}
                                        {res.customerPhone && (
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                            <span style={{ fontSize: '10px', color: '#6b7280' }}>{res.customerPhone}</span>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Court pill */}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
                                      <span style={getScheduleCourtPillStyle(res.courtId)}>{scheduleCourtLabel}</span>
                                    </div>
                                  </div>
                                </div>

                                {isAwaitingRes && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await updateReservation(res.id, { status: 'confirmed' });
                                      } catch (err) {
                                        await showAlert('Error', err instanceof Error ? err.message : 'Failed to approve', 'error');
                                      }
                                    }}
                                    style={{
                                      position: 'absolute', top: '3px', right: '3px',
                                      width: '20px', height: '20px', borderRadius: '4px',
                                      background: '#166534', color: '#fff', border: 'none',
                                      cursor: 'pointer', fontSize: '12px', fontWeight: 700,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      lineHeight: 1, padding: 0, zIndex: 11,
                                    }}
                                    title="Approve"
                                  >&#10003;</button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bookings Panel */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', width: '300px', flexShrink: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>
            Bookings
          </h3>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            {currentDateReservations.length} total &middot; {currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
          {currentDateReservations.length === 0 ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
              No bookings for this date
            </div>
          ) : (
            reservationSections.map(section => (
              <div key={section.key} style={{ marginBottom: '10px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px',
                  marginBottom: '6px',
                  background: '#f9fafb',
                  border: '1px solid #f3f4f6',
                  borderRadius: '6px',
                }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#374151', letterSpacing: '0.2px', textTransform: 'uppercase' }}>
                    {section.title}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280' }}>
                    {section.count}
                  </span>
                </div>
                {section.items.map(res => {
                const courtName = activeCourts.find(c => c.id === res.courtId)?.name || 'Unknown';
                const courtLabel = getCourtDisplayLabel(courtName);
                const fmtTime = (t: string) => {
                  const [h, m] = t.split(':').map(Number);
                  const suffix = h >= 12 ? 'p' : 'a';
                  const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
                  return m === 0 ? `${hr}${suffix}` : `${hr}:${m.toString().padStart(2, '0')}${suffix}`;
                };
                const tone = getReservationTone(res.status);

                return (
                  <div
                    key={res.id}
                    onClick={() => {
                      setSelectedReservation(res);
                      setFormData({
                        courtId: res.courtId,
                        customerName: res.customerName,
                        customerEmail: res.customerEmail || '',
                        customerPhone: res.customerPhone || '',
                        date: res.date,
                        startTime: res.startTime,
                        durationHours: res.durationHours,
                        status: res.status,
                        source: res.source,
                        notes: res.notes || '',
                      });
                      setIsEditing(false);
                      setShowEditModal(true);
                    }}
                    style={{
                      padding: '0', borderRadius: '10px', marginBottom: '8px',
                      background: '#fff',
                      border: `1px solid ${tone.border}22`,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
                      cursor: 'pointer', transition: 'all 0.15s ease',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    {/* Color accent bar */}
                    <div style={{ height: '3px', background: tone.border, opacity: tone.isPending ? 0.6 : 1 }} />

                    <div style={{ padding: '10px 12px' }}>
                      {/* Header: time + status */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={tone.border} strokeWidth="2.5" style={{ flexShrink: 0, opacity: 0.7 }}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>
                            {fmtTime(res.startTime)} - {fmtTime(res.endTime)}
                          </span>
                        </div>
                        <span style={{
                          fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px',
                          letterSpacing: '0.3px', textTransform: 'uppercase',
                          background: tone.badgeBg, color: tone.badgeText,
                        }}>
                          {tone.label}
                        </span>
                      </div>

                      {/* Customer name */}
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.01em' }}>
                        {res.customerName}
                      </div>

                      {/* Contact info */}
                      {(res.customerEmail || res.customerPhone) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '6px' }}>
                          {res.customerEmail && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                              <span style={{ fontSize: '11px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{res.customerEmail}</span>
                            </div>
                          )}
                          {res.customerPhone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ flexShrink: 0 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                              <span style={{ fontSize: '11px', color: '#6b7280' }}>{res.customerPhone}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Court pill + confirmation code */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                        <span style={getCourtPillStyle(res.courtId)}>{courtLabel}</span>
                        {res.confirmationCode && (
                          <span style={{ fontSize: '10px', color: '#b0b5be', fontFamily: 'monospace', letterSpacing: '0.3px' }}>
                            {res.confirmationCode}
                          </span>
                        )}
                      </div>

                      {/* Approve / Decline buttons */}
                      {PENDING_STATUSES.has(res.status.toLowerCase()) && (
                        <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }} onClick={e => e.stopPropagation()}>
                          <button
                            onClick={async () => {
                              try {
                                await updateReservation(res.id, { status: 'confirmed' });
                              } catch (err) {
                                await showAlert('Error', err instanceof Error ? err.message : 'Failed to approve', 'error');
                              }
                            }}
                            style={{
                              flex: 1, padding: '5px 8px', fontSize: '11px', fontWeight: 600,
                              background: '#166534', color: '#fff', border: 'none', borderRadius: '6px',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = '#14532d')}
                            onMouseLeave={e => (e.currentTarget.style.background = '#166534')}
                          >
                            <span>&#10003;</span> Approve
                          </button>
                          <button
                            onClick={async () => {
                              const confirmed = await showConfirm(
                                'Decline Reservation',
                                `Decline reservation for ${res.customerName}?`,
                                { confirmLabel: 'Decline', type: 'warning' }
                              );
                              if (confirmed) {
                                try {
                                  await updateReservation(res.id, { status: 'canceled' });
                                } catch (err) {
                                  await showAlert('Error', err instanceof Error ? err.message : 'Failed to decline', 'error');
                                }
                              }
                            }}
                            style={{
                              flex: 1, padding: '5px 8px', fontSize: '11px', fontWeight: 600,
                              background: '#fff', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#f87171'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#fecaca'; }}
                          >
                            <span>&#10005;</span> Decline
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            ))
          )}
        </div>
      </div>
      </div>

      {/* Edit Reservation Modal */}
      {showEditModal && selectedReservation && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit Reservation' : 'Reservation Details'}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Court *</label>
                <select
                  value={formData.courtId}
                  onChange={e => setFormData({ ...formData, courtId: e.target.value })}
                  disabled={!isEditing}
                >
                  {activeCourts.map(court => (
                    <option key={court.id} value={court.id}>{court.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={e => setFormData({ ...formData, customerName: e.target.value })}
                  disabled={!isEditing}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={e => setFormData({ ...formData, customerEmail: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={e => setFormData({ ...formData, customerPhone: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={e => setFormData({ ...formData, date: e.target.value })}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Start Time *</label>
                  <select
                    value={formData.startTime}
                    onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                    disabled={!isEditing}
                  >
                    {timeSlots.map(({ hour: h, minute: m }) => {
                      const val = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                      const label = `${h > 12 ? h - 12 : h}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>
                <div className="form-group">
                  <label>Duration</label>
                  <select
                    value={formData.durationHours}
                    onChange={e => setFormData({ ...formData, durationHours: parseFloat(e.target.value) })}
                    disabled={!isEditing}
                  >
                    <option value={0.5}>30 min</option>
                    <option value={1}>1 hour</option>
                    <option value={1.5}>1.5 hours</option>
                    <option value={2}>2 hours</option>
                    <option value={3}>3 hours</option>
                    <option value={4}>4 hours</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value as ReservationStatus })}
                    disabled={!isEditing}
                  >
                    <option value="pending_approval">Awaiting Approval</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="pending">Pending</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={formData.source}
                    onChange={e => setFormData({ ...formData, source: e.target.value as ReservationSource })}
                    disabled={!isEditing || selectedReservation.source === 'app'}
                  >
                    <option value="app">App</option>
                    <option value="manual">Manual Entry</option>
                    <option value="phone">Phone</option>
                    <option value="walkin">Walk-in</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  disabled={!isEditing}
                />
              </div>
              <div style={{ padding: '12px', background: 'var(--gray-100)', borderRadius: '8px', marginTop: '16px' }}>
                <p style={{ fontSize: '14px', color: 'var(--gray-600)', margin: 0 }}>
                  <strong>Total:</strong> ${selectedReservation.totalAmount} • <strong>Booked:</strong> {new Date(selectedReservation.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="modal-footer">
              {isEditing ? (
                <>
                  {selectedReservation.status !== 'canceled' && (
                    <button className="btn btn-danger" onClick={handleCancelReservation} style={{ marginRight: 'auto' }}>
                      Cancel Reservation
                    </button>
                  )}
                  <button className="btn btn-secondary" onClick={() => {
                    setIsEditing(false);
                    setFormData({
                      courtId: selectedReservation.courtId,
                      customerName: selectedReservation.customerName,
                      customerEmail: selectedReservation.customerEmail || '',
                      customerPhone: selectedReservation.customerPhone || '',
                      date: selectedReservation.date,
                      startTime: selectedReservation.startTime,
                      durationHours: selectedReservation.durationHours,
                      status: selectedReservation.status,
                      source: selectedReservation.source,
                      notes: selectedReservation.notes || '',
                    });
                  }}>
                    Cancel Edit
                  </button>
                  <button className="btn btn-primary" onClick={handleUpdateReservation}>Save Changes</button>
                </>
              ) : (
                <>
                  {(selectedReservation.status === 'pending_approval' || selectedReservation.status === 'pending') && (
                    <>
                      <button
                        className="btn btn-primary"
                        onClick={async () => {
                          try {
                            await updateReservation(selectedReservation.id, { status: 'confirmed' });
                            setShowEditModal(false);
                            setSelectedReservation(null);
                          } catch (err) {
                            await showAlert('Error', err instanceof Error ? err.message : 'Failed to approve', 'error');
                          }
                        }}
                        style={{ marginRight: 'auto' }}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ color: '#991b1b', borderColor: '#fecaca' }}
                        onClick={async () => {
                          const confirmed = await showConfirm(
                            'Decline Reservation',
                            `Decline reservation for ${selectedReservation.customerName}?`,
                            { confirmLabel: 'Decline', type: 'warning' }
                          );
                          if (confirmed) {
                            try {
                              await updateReservation(selectedReservation.id, { status: 'canceled' });
                              setShowEditModal(false);
                              setSelectedReservation(null);
                            } catch (err) {
                              await showAlert('Error', err instanceof Error ? err.message : 'Failed to decline', 'error');
                            }
                          }
                        }}
                      >
                        Decline
                      </button>
                    </>
                  )}
                  {selectedReservation.status !== 'canceled' && selectedReservation.status !== 'pending_approval' && selectedReservation.status !== 'pending' && (
                    <button className="btn btn-secondary" onClick={handleCancelReservation} style={{ marginRight: 'auto' }}>
                      Cancel Reservation
                    </button>
                  )}
                  <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Spinner animation for sync */}
      {syncing && (
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      )}
    </DashboardLayout>
  );
}
